/**
 * The company that issues a workspace's documents (Settings → Company): structured fields on the
 * workspace, edited by its owner. A finalised invoice keeps a snapshot in `sender` (and the logo
 * in its own `companyLogo`), so later edits never change it; drafts, quotes and templates print
 * the company as it is. Mirrors server/pb_hooks/lib/company.js; the PDF service formats the
 * printed block from these fields (services/pdf/src/blanqo/sender.ts).
 */
import { z } from "zod";
import type { WorkspacesResponse } from "@/lib/core/pb-types";
import { normalizeVatId, vatIdExample, vatIdIssue, type VatIssue } from "@/lib/customer/vat";

export const COMPANY_FIELDS = [
  "name",
  "displayName",
  "street",
  "addressLine2",
  "postalCode",
  "city",
  "country",
  "vatId",
  "taxNumber",
  "contactName",
  "phone",
  "email",
  "website",
  "register",
  "directors",
  "extra",
  "logoWidth",
] as const;
export type CompanyField = (typeof COMPANY_FIELDS)[number];

/** What a document prints as its sender: the company's fields, every one a string. */
export type Sender = Record<CompanyField, string>;

/** The workspace's company: its fields, the logo file and whether the owner has checked it. */
export type Company = Sender & { workspace: string; logo: string; reviewed: boolean };

export const EMPTY_SENDER = Object.fromEntries(COMPANY_FIELDS.map((f) => [f, ""])) as Sender;

/** "name" -> "companyName": the field on the workspace record. */
export const workspaceField = (field: CompanyField) =>
  `company${field.charAt(0).toUpperCase()}${field.slice(1)}` as const;

type WorkspaceCompany = Pick<WorkspacesResponse, "id"> & Partial<Record<string, unknown>>;

export function companyOf(workspace: WorkspaceCompany): Company {
  const fields = Object.fromEntries(
    COMPANY_FIELDS.map((field) => [field, String(workspace[workspaceField(field)] ?? "")]),
  ) as Sender;
  return {
    ...fields,
    workspace: workspace.id,
    logo: String(workspace.companyLogo ?? ""),
    reviewed: Boolean(workspace.companyReviewed),
  };
}

/** A finalised invoice's snapshot of its sender, or null for documents that print the live one. */
export function snapshotOf(record: { sender?: unknown }): Sender | null {
  const value = record.sender;
  if (!value || typeof value !== "object" || !("name" in value)) return null;
  const stored = value as Partial<Record<string, unknown>>;
  return Object.fromEntries(
    COMPANY_FIELDS.map((field) => [field, String(stored[field] ?? "")]),
  ) as Sender;
}

/** The sender of a document: its snapshot once finalised, else the workspace's company now. */
export const senderOf = (record: { sender?: unknown }, company: Company | null | undefined) =>
  snapshotOf(record) ?? company ?? EMPTY_SENDER;

/** Same-origin path of the logo the document prints ("" = none), from the same source. */
export function logoPathOf(
  record: { id: string; sender?: unknown; companyLogo?: string },
  company: Company | null | undefined,
): string {
  if (snapshotOf(record)) {
    return record.companyLogo
      ? `/api/files/invoices/${record.id}/${encodeURIComponent(record.companyLogo)}`
      : "";
  }
  return company?.logo ? companyLogoPath(company.workspace, company.logo) : "";
}

export const companyLogoPath = (workspaceId: string, file: string) =>
  file ? `/api/files/workspaces/${workspaceId}/${encodeURIComponent(file)}` : "";

/** The name emails and placeholders use: the display name, else the legal name. */
export const displayNameOf = (sender: Pick<Sender, "displayName" | "name">) =>
  sender.displayName || sender.name;

/** The PDF payload's `sender` (the service builds the printed block from it). */
export function pdfSender(sender: Sender) {
  const { displayName: _display, contactName: _contact, logoWidth: _logo, ...printed } = sender;
  return printed;
}

/**
 * Settings → Company. Built per render so messages follow the UI language. The VAT ID is printed
 * on every invoice, so an EU number gets the same format check as a customer's.
 */
export function companySchema(messages: {
  email: string;
  country: string;
  vat: (issue: VatIssue, example: string) => string;
}) {
  const text = (max: number) => z.string().trim().max(max);
  return z
    .object({
      name: text(300),
      displayName: text(200),
      street: text(300),
      addressLine2: text(300),
      postalCode: text(20),
      city: text(200),
      country: z.string().regex(/^([A-Z]{2})?$/, messages.country),
      vatId: text(30).transform(normalizeVatId),
      taxNumber: text(30),
      contactName: text(200),
      phone: text(50),
      email: text(200).refine((v) => !v || z.email().safeParse(v).success, messages.email),
      website: text(200),
      register: text(300),
      directors: text(300),
      extra: text(2000),
      logoWidth: text(10),
    })
    .superRefine((values, ctx) => {
      const issue = vatIdIssue(values.vatId, values.country);
      if (issue) {
        const example = vatIdExample(values.country) || "DE123456789";
        ctx.addIssue({ code: "custom", path: ["vatId"], message: messages.vat(issue, example) });
      }
    });
}
export type CompanyFormValues = z.infer<ReturnType<typeof companySchema>>;
